import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/** Where an offer placement is displayed. Placements are one-per-surface. */
export enum OfferPlacementSurface {
  TRACKING_PAGE = "tracking-page",
  RETURNS_CONFIRMATION = "returns-confirmation",
  HOSTED_PAGE = "hosted-page",
  SHOPIFY_THANK_YOU = "shopify-thank-you",
}

export const prettyOfferPlacementSurface = {
  [OfferPlacementSurface.TRACKING_PAGE]: "Tracking page",
  [OfferPlacementSurface.RETURNS_CONFIRMATION]: "Returns confirmation",
  [OfferPlacementSurface.HOSTED_PAGE]: "Hosted page",
  [OfferPlacementSurface.SHOPIFY_THANK_YOU]: "Shopify thank-you page",
};

/** Values are Falcon's exact accepted API enum (docs.falconlabs.com). */
export enum FalconAgeDemographic {
  UNDER_18 = "under_18",
  AGE_18_24 = "age_18_to_24",
  AGE_25_34 = "age_25_to_34",
  AGE_35_44 = "age_35_to_44",
  AGE_45_54 = "age_45_to_54",
  AGE_55_64 = "age_55_to_64",
  OVER_65 = "over_65",
  MIXED = "mixed",
}

export const prettyFalconAgeDemographic = {
  [FalconAgeDemographic.UNDER_18]: "Under 18",
  [FalconAgeDemographic.AGE_18_24]: "18–24",
  [FalconAgeDemographic.AGE_25_34]: "25–34",
  [FalconAgeDemographic.AGE_35_44]: "35–44",
  [FalconAgeDemographic.AGE_45_54]: "45–54",
  [FalconAgeDemographic.AGE_55_64]: "55–64",
  [FalconAgeDemographic.OVER_65]: "65+",
  [FalconAgeDemographic.MIXED]: "Mixed / all ages",
};

export enum FalconGenderDemographic {
  MALE = "male",
  FEMALE = "female",
  MIXED = "mixed",
  OTHER = "other",
}

export const prettyFalconGenderDemographic = {
  [FalconGenderDemographic.MALE]: "Mostly male",
  [FalconGenderDemographic.FEMALE]: "Mostly female",
  [FalconGenderDemographic.MIXED]: "Mixed",
  [FalconGenderDemographic.OTHER]: "Other",
};

const falconSiteSettingsSchema_ = z.object({
  // `.catch` keeps a stored value Falcon's enum no longer recognizes from
  // failing the whole integration parse (which would drop its placements).
  ageDemographics: createEnum(Object.values(FalconAgeDemographic))
    .optional()
    .catch(undefined),
  genderDemographics: createEnum(Object.values(FalconGenderDemographic))
    .optional()
    .catch(undefined),
  categoryName: z.string().optional(),
});
export type FalconSiteSettings = z.infer<typeof falconSiteSettingsSchema_>;
export const falconSiteSettingsSchema: z.ZodType<FalconSiteSettings> =
  falconSiteSettingsSchema_;

const roktOfferPlacementSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  surface: createEnum(Object.values(OfferPlacementSurface)),
  /** Page identifier issued by the merchant's Rokt contact. */
  pageIdentifier: z.string(),
  sandbox: z.boolean(),
});
export const roktOfferPlacementSchema: z.ZodType<RoktOfferPlacement> =
  roktOfferPlacementSchema_;
export interface RoktOfferPlacement extends z.infer<
  typeof roktOfferPlacementSchema_
> {}

const falconOfferPlacementSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  surface: createEnum(Object.values(OfferPlacementSurface)),
  /** Falcon's vendor placement id, returned by their placements API. */
  falconPlacementId: z.string(),
  /** false = Falcon serves mock/test offers (no revenue or attribution). */
  isLiveMode: z.boolean(),
});
export const falconOfferPlacementSchema: z.ZodType<FalconOfferPlacement> =
  falconOfferPlacementSchema_;
export interface FalconOfferPlacement extends z.infer<
  typeof falconOfferPlacementSchema_
> {}
